import { useEffect, useMemo, useState } from 'react';
import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';
import StatusBadge from '@/components/base/StatusBadge';
import { formatDate } from '@/utils/format';
import { summarizeChecks } from '@/utils/deliverable';
import type {
  CampaignDeliverable,
  DeliverableDecision,
  DeliverableFeedback,
  DeliverableReview,
} from '@/types/portal';
import PlatformIcon from '@/components/base/PlatformIcon';
import DeliverableChecks from './DeliverableChecks';
import DeliverableFeedbackThread from './DeliverableFeedbackThread';

type ComposerMode = 'idle' | 'approve' | 'changes' | 'comment';

interface DeliverableReviewModalProps {
  deliverable: CampaignDeliverable | null;
  review?: DeliverableReview;
  status: string;
  thread: DeliverableFeedback[];
  onClose: () => void;
  onDecide: (decision: DeliverableDecision, text: string, timecode?: string) => void;
  /** False for roles without `campaigns.approve_content`: the review is read-only. */
  canReview?: boolean;
  /** Last save error from the API, shown above the actions. */
  error?: string;
}

const COMPOSER_COPY: Record<
  Exclude<ComposerMode, 'idle'>,
  { title: string; placeholder: string; confirm: string; icon: string; needsText: boolean; fallback: string }
> = {
  approve: {
    title: 'Approve with a note (optional)',
    placeholder: 'Add context for the creator and the audit trail...',
    confirm: 'Approve',
    icon: 'ri-check-line',
    needsText: false,
    fallback: 'Approved this deliverable.',
  },
  changes: {
    title: 'What needs to change?',
    placeholder: 'Be specific — the creator will see this before resubmitting...',
    confirm: 'Send feedback',
    icon: 'ri-edit-2-line',
    needsText: true,
    fallback: '',
  },
  comment: {
    title: 'Add a note',
    placeholder: 'Ask a question or flag something for the creator...',
    confirm: 'Post note',
    icon: 'ri-chat-3-line',
    needsText: true,
    fallback: '',
  },
};

function decisionFor(mode: Exclude<ComposerMode, 'idle'>): DeliverableDecision {
  if (mode === 'approve') return 'approved';
  if (mode === 'changes') return 'changes_requested';
  return 'comment';
}

export default function DeliverableReviewModal({
  deliverable,
  review,
  status,
  thread,
  onClose,
  onDecide,
  canReview = true,
  error = '',
}: DeliverableReviewModalProps) {
  const [versionIndex, setVersionIndex] = useState(0);
  const [mode, setMode] = useState<ComposerMode>('idle');
  const [note, setNote] = useState('');
  const [timecode, setTimecode] = useState('');

  const deliverableId = deliverable?.id;

  useEffect(() => {
    setVersionIndex(review ? Math.max(0, review.versions.length - 1) : 0);
    setMode('idle');
    setNote('');
    setTimecode('');
  }, [deliverableId, review]);

  const summary = useMemo(
    () => (review ? summarizeChecks(review.checks) : { passes: 0, warns: 0, fails: 0 }),
    [review],
  );

  const locked = status === 'Paid';
  const mediaUrl = review?.versions[versionIndex]?.mediaUrl ?? review?.mediaUrl;
  const vertical =
    !review?.aspectRatio ||
    review.aspectRatio.startsWith('9:16') ||
    review.aspectRatio.startsWith('4:5');

  const resetComposer = () => {
    setMode('idle');
    setNote('');
    setTimecode('');
  };

  const confirm = () => {
    if (mode === 'idle' || !deliverable) return;
    const copy = COMPOSER_COPY[mode];
    const text = note.trim() || copy.fallback;
    if (copy.needsText && !text) return;
    onDecide(decisionFor(mode), text, timecode);
    resetComposer();
  };

  const copy = mode === 'idle' ? null : COMPOSER_COPY[mode];
  const canConfirm = copy ? !copy.needsText || note.trim().length > 0 : false;

  return (
    <Modal
      open={Boolean(deliverable)}
      onClose={onClose}
      icon="ri-movie-line"
      title={deliverable?.type ?? 'Deliverable'}
      subtitle={
        deliverable
          ? `${deliverable.creatorName} · ${deliverable.platform} · due ${formatDate(deliverable.dueDate)}`
          : undefined
      }
      widthClassName="max-w-4xl"
      footer={
        !canReview ? (
          <div className="flex w-full items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-600">
            <i className="ri-eye-line" />
            Your role can view deliverables but not review them.
          </div>
        ) : locked ? (
          <div className="flex w-full items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-600">
            <i className="ri-lock-line" />
            This deliverable has been paid and is locked for review.
          </div>
        ) : mode === 'idle' ? (
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            {error ? (
              <p role="alert" className="flex items-center gap-1.5 text-xs text-primary-700">
                <i className="ri-error-warning-line" />
                {error}
              </p>
            ) : (
              <p className="flex items-center gap-1.5 text-xs text-foreground-500">
                <i className="ri-information-line" />
                Feedback is shared with the creator and saved to the audit trail.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button variant="ghost" size="sm" icon="ri-chat-3-line" onClick={() => setMode('comment')}>
                Add note
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon="ri-edit-2-line"
                onClick={() => setMode('changes')}
              >
                Request changes
              </Button>
              <Button
                variant="accent"
                size="sm"
                icon="ri-check-line"
                onClick={() => setMode('approve')}
                disabled={status === 'Approved'}
              >
                {status === 'Approved' ? 'Approved' : 'Approve'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="w-full space-y-2">
            <label
              htmlFor="deliverable-feedback-note"
              className="text-xs font-label font-semibold uppercase tracking-wider text-foreground-500"
            >
              {copy?.title}
            </label>
            <textarea
              id="deliverable-feedback-note"
              value={note}
              maxLength={500}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
              placeholder={copy?.placeholder}
              className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <label htmlFor="deliverable-timecode" className="text-xs text-foreground-500">
                  Timecode
                </label>
                <input
                  id="deliverable-timecode"
                  value={timecode}
                  onChange={(event) => setTimecode(event.target.value)}
                  placeholder="e.g. 0:12"
                  className="h-8 w-24 rounded-md border border-background-300 bg-background-50 px-2 text-xs text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
                <span className="text-[11px] text-foreground-400">{note.length}/500</span>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={resetComposer}>
                  Cancel
                </Button>
                <Button
                  variant={mode === 'approve' ? 'accent' : 'primary'}
                  size="sm"
                  icon={copy?.icon}
                  onClick={confirm}
                  disabled={!canConfirm}
                >
                  {copy?.confirm}
                </Button>
              </div>
            </div>
          </div>
        )
      }
    >
      {deliverable && review ? (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.35fr_1fr]">
          <div>
            <div
              className={`relative w-full overflow-hidden rounded-lg border border-background-200 bg-background-100 ${
                vertical ? 'h-[360px] md:h-[420px]' : 'h-[280px] md:h-[320px]'
              }`}
            >
              {mediaUrl ? (
                <img
                  src={mediaUrl}
                  decoding="async"
                  alt={`${deliverable.type} preview for ${deliverable.creatorName}`}
                  title={`${deliverable.type} — ${deliverable.platform}`}
                  className="h-full w-full object-cover object-top"
                />
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-center text-sm text-foreground-500">
                  <i className="ri-image-line text-3xl text-foreground-300" />
                  No media preview for this submission.
                  {review.postUrl ? (
                    <a
                      href={review.postUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-medium text-primary-700 hover:underline"
                    >
                      Open the published post
                    </a>
                  ) : null}
                </div>
              )}
              <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-foreground-950/70 px-2.5 py-1 text-[11px] font-label font-medium text-background-50">
                <PlatformIcon platform={deliverable.platform} className="text-[12px]" mono />
                {deliverable.platform}
              </span>
              {review.aspectRatio ? (
                <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-foreground-950/70 px-2.5 py-1 text-[11px] font-label font-medium text-background-50">
                  <i className="ri-aspect-ratio-line" />
                  {review.aspectRatio}
                </span>
              ) : null}
              {review.duration ? (
                <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-foreground-950/70 px-2.5 py-1 text-[11px] font-label font-medium text-background-50">
                  <i className="ri-time-line" />
                  {review.duration}
                </span>
              ) : null}
              {mediaUrl ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <button
                    type="button"
                    aria-label="Play preview"
                    className="flex h-14 w-14 cursor-pointer items-center justify-center rounded-full bg-background-50/90 text-2xl text-foreground-900 transition-transform hover:scale-105"
                  >
                    <i className="ri-play-fill" />
                  </button>
                </div>
              ) : null}
            </div>

            {review.versions.length > 1 ? (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
                  Versions
                </span>
                {review.versions.map((version, index) => (
                  <button
                    key={version.id}
                    type="button"
                    onClick={() => setVersionIndex(index)}
                    className={`cursor-pointer rounded-md border px-2.5 py-1 text-xs font-label font-medium transition-colors ${
                      index === versionIndex
                        ? 'border-foreground-900 bg-foreground-900 text-background-50'
                        : 'border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    v{version.version}
                  </button>
                ))}
              </div>
            ) : null}

            <section className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4">
              <h3 className="text-xs font-label font-semibold uppercase tracking-wider text-foreground-400">
                Creator caption
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">
                {review.caption}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {review.hashtags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-secondary-200 bg-secondary-100 px-2 py-0.5 text-[11px] font-label text-secondary-900"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              {review.versions.length > 0 ? (
                <p className="mt-3 flex items-center gap-1.5 text-[11px] text-foreground-400">
                  <i className="ri-upload-cloud-2-line" />
                  v{review.versions[versionIndex]?.version ?? 1} submitted{' '}
                  {formatDate(review.versions[versionIndex]?.submittedAt ?? review.submittedAt)} ·{' '}
                  {review.versions[versionIndex]?.note}
                </p>
              ) : null}
            </section>
          </div>

          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={status} />
              <span className="text-xs text-foreground-500">
                Submitted {formatDate(review.submittedAt)}
              </span>
            </div>

            <section className="rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-xs font-label font-semibold uppercase tracking-wider text-foreground-400">
                  Brief compliance
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-label text-accent-800">
                    {summary.passes} pass
                  </span>
                  {summary.warns > 0 ? (
                    <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-label text-secondary-900">
                      {summary.warns} warn
                    </span>
                  ) : null}
                  {summary.fails > 0 ? (
                    <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-label text-primary-800">
                      {summary.fails} fail
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="mt-3">
                <DeliverableChecks checks={review.checks} />
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h3 className="text-xs font-label font-semibold uppercase tracking-wider text-foreground-400">
                Feedback & activity
              </h3>
              <div className="mt-3">
                <DeliverableFeedbackThread items={thread} />
              </div>
            </section>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}